/**
 * Permit Discovery — SF application-type advisor (permit | planning | license).
 * Uses catalog-backed matching from public SF.gov guidance.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Stack,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import { ArrowBack as BackIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import FeeDisplay from '../../components/permit/FeeDisplay';
import { adviseApplicationType, getSfCatalog } from '../../utils/applicationTypeAdvisor';

const PROJECT_TYPES = [
  'New construction',
  'Renovation / Remodel',
  'Home business',
  'Commercial space',
  'Outdoor structure (deck, shed)',
  'Plumbing / Electrical',
  'Other',
];

const SCOPES = {
  small: 'Under $10,000',
  medium: '$10,000 – $50,000',
  large: '$50,000 – $500,000',
  xlarge: 'Over $500,000',
};

const TRACK_COLORS = {
  permit: 'primary',
  planning: 'secondary',
  license: 'warning',
};

const FEE_TYPE_BY_APP = {
  building_permit: 'building',
  electrical_permit: 'electrical',
  plumbing_permit: 'plumbing',
  mechanical_permit: 'electrical',
  solar_permit: 'building',
  business_registration: 'business-license',
  activity_permit: 'business-license',
  zoning_compliance: 'zoning',
  change_of_use: 'zoning',
  adu_planning: 'adu',
  exempt_no_permit: 'building',
};

export default function PermitWizardDemo({ onBack }) {
  const catalog = useMemo(() => getSfCatalog(), []);
  const [step, setStep] = useState(0);
  const [projectType, setProjectType] = useState('');
  const [location, setLocation] = useState('San Francisco, CA');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('');

  const advice = useMemo(() => {
    if (step < 3 && !(projectType && location && scope)) return null;
    return adviseApplicationType({
      projectType,
      category: projectType,
      location,
      description: description || projectType,
    });
  }, [projectType, location, description, scope, step]);

  const routing = advice?.result?.routing;
  const steps = ['Project type', 'Location', 'Details'];
  const canProceed = step === 0 ? projectType : step === 1 ? location.trim() : scope;

  return (
    <Paper sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <Button size="small" startIcon={<BackIcon />} onClick={onBack}>
              Back
            </Button>
          )}
          <Typography variant="h6" sx={{ flex: 1 }}>
            SF application advisor
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Describe your project in {catalog.name}. We recommend whether to start with a
          {' '}
          <strong>building permit</strong>,
          {' '}
          <strong>planning / zoning</strong>, or
          {' '}
          <strong>business registration</strong>
          {' '}
          using public City guidance.
        </Typography>

        <Stepper activeStep={Math.min(step, 2)} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <FormControl fullWidth>
            <InputLabel>What type of project?</InputLabel>
            <Select
              value={projectType}
              label="What type of project?"
              onChange={(e) => setProjectType(e.target.value)}
            >
              {PROJECT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {step === 1 && (
          <TextField
            fullWidth
            label="Where is the project?"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="San Francisco address or neighborhood"
            helperText="Catalog is San Francisco–specific; other cities coming later."
          />
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Describe the work (optional but recommended)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Build an ADU, open a home bakery, add a rear deck…"
            />
            <FormControl fullWidth>
              <InputLabel>Estimated project cost</InputLabel>
              <Select value={scope} label="Estimated project cost" onChange={(e) => setScope(e.target.value)}>
                {Object.entries(SCOPES).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        {step < 2 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!canProceed}>
            Next
          </Button>
        ) : step === 2 ? (
          <Button
            variant="contained"
            onClick={() => setStep(3)}
            disabled={!canProceed}
          >
            Get recommendation
          </Button>
        ) : routing ? (
          <Box>
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              Primary path:
              {' '}
              <strong>{routing.primaryTrackLabel}</strong>
              {routing.confidence != null && (
                <>
                  {' '}
                  · confidence
                  {' '}
                  {Math.round(routing.confidence * 100)}
                  %
                </>
              )}
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>{routing.rationale}</Typography>

            <Typography variant="subtitle2" gutterBottom>
              Applications to consider
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {routing.applicationTypes.map((t) => (
                <Paper key={t.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" fontWeight={600}>{t.label}</Typography>
                    <Chip
                      label={t.track}
                      size="small"
                      color={TRACK_COLORS[t.track] || 'default'}
                    />
                    {t.track === routing.primaryTrack && (
                      <Chip label="Primary track" size="small" variant="outlined" />
                    )}
                  </Stack>
                  {t.typical && (
                    <Chip label={`Typical: ${t.typical}`} size="small" sx={{ mt: 1 }} />
                  )}
                </Paper>
              ))}
            </Stack>

            {routing.nextSteps?.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>Next steps</Typography>
                <Box component="ul" sx={{ mt: 0, pl: 2 }}>
                  {routing.nextSteps.map((s) => (
                    <Typography component="li" variant="body2" key={s} sx={{ mb: 0.5 }}>{s}</Typography>
                  ))}
                </Box>
              </>
            )}

            {routing.sources?.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Public sources</Typography>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  {routing.sources.map((s) => (
                    <Link key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" variant="body2">
                      {s.title}
                    </Link>
                  ))}
                </Stack>
              </>
            )}

            <FeeDisplay
              permitType={FEE_TYPE_BY_APP[routing.applicationTypes[0]?.id] || 'building'}
              scope={scope}
              sqft={scope === 'large' ? 2000 : scope === 'xlarge' ? 5000 : scope === 'medium' ? 500 : null}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              {routing.disclaimer}
            </Alert>

            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => {
                setStep(0);
                setProjectType('');
                setLocation('San Francisco, CA');
                setDescription('');
                setScope('');
              }}
            >
              Start over
            </Button>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}
